import { authenticateUserCredentials } from "../utils";
import {
    AuthLeftContainer,
    AuthLeftInnerContainer,
    AuthMainContainer,
    AuthRightContainer,
} from "../components/styled/Containers";
import { AuthForm, AuthInputWithLabel, AuthSubmitButton } from "../components/styled/Forms";
import AuthKnowtTabHeader from "../components/KnowtTabHeader";
import { AuthTitle } from "../components/styled/Typographies";
import { GapPerSection } from "../components/styled/Separator";
import { callIsUsernameAvailable, callUpdateUserEmail, getUserPlan } from "@knowt/syncing/hooks/user/graphqlUtils";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { updateUserUsername } from "@knowt/syncing/hooks/user/utils";
import { getUserNameErrors, validateUsername } from "@knowt/syncing/hooks/user/userVerificationUtils";
import { SubscriptionType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { cleanEmail, signIn } from "@knowt/syncing/hooks/user/auth";
import { FlexRowAlignCenter } from "@/components/Flex";
import CircularOutlineIcon from "@/components/CircularButton/styled/CircularOutlineIcon";
import { X } from "lucide-react";
import br from "@/styles/breakpoints.module.css";
import { useDebouncedCallback } from "@knowt/syncing/utils/hooks/useDebouncedCallback";

export enum ChangeAuthDetailsType {
    EMAIL = "email",
    USERNAME = "username",
}

type ChangeAuthDetailsPageProps = {
    onSubmit: () => void;
    onClose: () => void;
    type: ChangeAuthDetailsType;
};

const ChangeAuthDetailsPage = ({ onSubmit, onClose, type }: ChangeAuthDetailsPageProps) => {
    const { user } = useCurrentUser();

    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);
    const [submitStatus, setSubmitStatus] = useState<"loading" | "success" | "failure" | null>(null);

    const [changeAuthDetailsStates, updateChangeAuthDetailsStates] = useCombinedState({
        currentEmail: "",
        currentPassword: "",
        newEmail: "",
        newUsername: "",
        isUsernameAvailable: true,
    });

    const checkIfUserNameAvailable = useDebouncedCallback(username => {
        if (username === "" || username === user?.username) {
            return updateChangeAuthDetailsStates({ isUsernameAvailable: true });
        }

        callIsUsernameAvailable(username).then(isAvailable => {
            updateChangeAuthDetailsStates({ isUsernameAvailable: !!isAvailable });
        });
    }, 500);

    useEffect(() => {
        if (type === ChangeAuthDetailsType.USERNAME) {
            checkIfUserNameAvailable(changeAuthDetailsStates.newUsername);
        }
    }, [user?.username, changeAuthDetailsStates.newUsername, updateChangeAuthDetailsStates]);

    const _onSubmit = () => {
        setTimeout(() => {
            setSubmitStatus("success");
            onSubmit();
        }, 1000);
    };

    const authenticateCurrentCredentials = async () => {
        if (!user) {
            setSubmitStatus(null);
            toast.error("Something went wrong. Please try again.");
            return false;
        }

        if (!changeAuthDetailsStates.currentEmail || !changeAuthDetailsStates.currentPassword) {
            setSubmitStatus(null);
            toast.error("Please enter your email and password.");
            return false;
        }

        if (changeAuthDetailsStates.currentEmail !== user.Email) {
            setSubmitStatus(null);
            toast.error("Your current email doesn't match your account.");
            return false;
        }

        try {
            await authenticateUserCredentials({
                email: changeAuthDetailsStates.currentEmail,
                password: changeAuthDetailsStates.currentPassword,
            });
        } catch {
            toast.error("Your credentials were incorrect. Please try typing in your email or password again!");
            setSubmitStatus(null);
            return false;
        }

        return true;
    };

    const onChangeEmail = async () => {
        toast.loading("Updating your email...", {
            id: "change-email",
        });
        try {
            await authenticateCurrentCredentials();
            const cleanedEmail = cleanEmail(changeAuthDetailsStates.newEmail);

            await callUpdateUserEmail({
                newEmail: cleanedEmail,
                password: changeAuthDetailsStates.currentPassword,
            });

            // if successful, sign in with the new email
            await signIn(changeAuthDetailsStates.newEmail, changeAuthDetailsStates.currentPassword);
            toast.success("Your email has been updated!", {
                id: "change-email",
            });
            _onSubmit();
        } catch (e) {
            toast.error(e?.message ?? "Something went wrong, please try again.", {
                id: "change-email",
            });
            setSubmitStatus(null);
        }
    };

    const onChangeUsername = async () => {
        if (!user) return;
        const subscriptionType = getUserPlan(user);

        const isUsernameAvailable = changeAuthDetailsStates.isUsernameAvailable
            ? await callIsUsernameAvailable(changeAuthDetailsStates.newUsername)
            : false;

        if (!changeAuthDetailsStates.isUsernameAvailable || !isUsernameAvailable) {
            setSubmitStatus(null);
            return toast.error("This username is already taken.");
        }

        try {
            validateUsername(changeAuthDetailsStates.newUsername);

            if (subscriptionType === SubscriptionType.BASIC && (user.usernameChange ?? 0) > 0) {
                openUpgradePopup({
                    event: UpgradeEvent.USERNAME_CHANGE,
                    context: {},
                    onClose: () => onClose,
                });
                return setSubmitStatus(null);
            }

            await updateUserUsername(changeAuthDetailsStates.newUsername);
            _onSubmit();
        } catch (e) {
            toast.error(e?.[0]?.message || e?.message || "Something went wrong, please try again.");
            setSubmitStatus(null);
        }
    };

    const handleSubmit = () => {
        setSubmitStatus("loading");
        if (type === ChangeAuthDetailsType.EMAIL) {
            onChangeEmail();
        } else if (type === ChangeAuthDetailsType.USERNAME) {
            onChangeUsername();
        }
    };

    const getForm = () => (
        <AuthForm onSubmit={handleSubmit}>
            <GapPerSection />
            {type === ChangeAuthDetailsType.EMAIL && (
                <>
                    <AuthInputWithLabel
                        label="Enter your current email"
                        type="email"
                        value={changeAuthDetailsStates.currentEmail}
                        onChange={e => updateChangeAuthDetailsStates({ currentEmail: e.target.value })}
                        placeholder={"Enter your current email"}
                    />
                    <GapPerSection />
                    <AuthInputWithLabel
                        label="Enter your Knowt password"
                        type="password"
                        value={changeAuthDetailsStates.currentPassword}
                        onChange={e =>
                            updateChangeAuthDetailsStates({
                                currentPassword: e.target.value,
                            })
                        }
                        placeholder={"Enter your Knowt password"}
                    />
                </>
            )}
            <GapPerSection />
            {type === ChangeAuthDetailsType.EMAIL ? (
                <AuthInputWithLabel
                    label="Enter the new email"
                    type="email"
                    value={changeAuthDetailsStates.newEmail}
                    onChange={e =>
                        updateChangeAuthDetailsStates({
                            newEmail: e.target.value,
                        })
                    }
                    placeholder={"Enter the new email"}
                />
            ) : type === ChangeAuthDetailsType.USERNAME ? (
                <AuthInputWithLabel
                    label="Enter the new username"
                    type="text"
                    value={changeAuthDetailsStates.newUsername}
                    onChange={e => {
                        updateChangeAuthDetailsStates({ newUsername: e.target.value });
                        checkIfUserNameAvailable(e.target.value);
                    }}
                    placeholder={"Enter the new username"}
                    errors={
                        [
                            getUserNameErrors(changeAuthDetailsStates.newUsername)?.replace(
                                "Please enter a username.",
                                ""
                            ),
                            changeAuthDetailsStates.isUsernameAvailable ? "" : "This username is already taken.",
                        ].filter(Boolean) as string[]
                    }
                />
            ) : null}
            <GapPerSection />
            <AuthSubmitButton authStatus={submitStatus}>
                {type === ChangeAuthDetailsType.EMAIL ? "Update my email" : "Update my username"}
            </AuthSubmitButton>
            <GapPerSection />
        </AuthForm>
    );

    return (
        <AuthMainContainer>
            <AuthLeftContainer>
                <FlexRowAlignCenter>
                    <AuthKnowtTabHeader hideTab />
                    <CircularOutlineIcon
                        onClick={onClose}
                        radius="4.7rem"
                        Icon={X}
                        size={30}
                        sx={{ marginLeft: "auto", marginRight: "2rem" }}
                        className={br.mdUpDisplayNone}
                    />
                </FlexRowAlignCenter>
                <AuthLeftInnerContainer style={{ flex: 1, justifyContent: "center" }}>
                    <AuthTitle>
                        {type === ChangeAuthDetailsType.EMAIL ? "Change your email" : "Change your username"}
                    </AuthTitle>
                    {getForm()}
                </AuthLeftInnerContainer>
            </AuthLeftContainer>
            <AuthRightContainer onClose={onClose} />
        </AuthMainContainer>
    );
};

export default ChangeAuthDetailsPage;
